import type { GameModel, ReadOnlyGameModel } from 'yage/game/GameModel';
import { validateTerminalRecord, airportRun, type TerminalRecord } from '../domain/terminal-record.js';
import { captureTerminal } from './terminal.js';
import { advanceManaged, catchUpManaged, startManaging } from '../sim/flow/managed.js';
import { state, career } from './world.js';

/** Earlier airports owned by this career, each a validated frozen terminal record. Corrupt runtime data yields none. */
export function retainedAirports(g: GameModel | ReadOnlyGameModel): TerminalRecord[] {
    let raw: unknown;
    try { raw = JSON.parse(career(g).retainedAirports); } catch { return []; }
    if (!Array.isArray(raw)) return [];
    const records: TerminalRecord[] = [];
    for (const item of raw) { const checked = validateTerminalRecord(item, { people: new Set() }); if (checked.ok) records.push(checked.record); }
    return records;
}

/**
 * Keep the current terminal as a retained airport before a graduation reset. Items a captain carries stay at
 * that airport, dropped where they are, so moving on is never free disposal. Returns an error instead of losing state.
 */
export function retainCurrentTerminal(g: GameModel): { ok: true } | { ok: false; error: string } {
    const captured = captureTerminal(g);
    if (!captured.ok) return captured;
    const record = captured.record;
    for (const item of [...record.bags, ...record.litter]) if (item.carrier?.kind === 'person') item.carrier = null;
    const kept = retainedAirports(g).filter(other => other.airportId !== record.airportId);
    kept.push(record);
    kept.sort((a, b) => airportRun(a.airportId) - airportRun(b.airportId));
    // The Promotion: leaving Summit Ridge ends Act I, and from then on every airport the career owns runs managed.
    const promoted = airportRun(record.airportId) >= airportRun(PROMOTION_AIRPORT) ? promote(kept, career(g).prestige, g.frame) : kept;
    career(g).retainedAirports = JSON.stringify(promoted);
    return { ok: true };
}

/** Act I's last airport; until a career leaves it, airports it left stay frozen (R2-03). */
import { PROMOTION_AIRPORT } from '../domain/missions.js';
export { PROMOTION_AIRPORT };

/** Deterministic per-airport L1 seed, so every peer starts the same managed stream. */
export function managedSeed(airportId: string, frame: number): number {
    let hash = 2166136261;
    for (const char of `${airportId}:${frame}`) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
    return hash;
}

/** Hand every frozen retained airport to L1. An airport L1 cannot run (for example an owned airline) stays frozen. */
export function promote(records: TerminalRecord[], prestige: number, frame: number): TerminalRecord[] {
    return records.map(r => {
        if (r.managed) return r;
        const started = startManaging(r, 'active', { prestige, randomState: managedSeed(r.airportId, frame) });
        return started.ok ? started.record : r;
    });
}

/** Move managed airports' waiting floor cash out of their records; the caller banks the returned total. */
export function sweepManagedCash(g: GameModel): { total: number; airports: string[] } {
    const records = retainedAirports(g);
    let total = 0;
    const airports: string[] = [];
    for (const r of records) {
        if (!r.managed) continue;
        const amount = [...r.stations, ...r.vending].reduce((n, account) => n + account.pending, 0);
        if (!amount) continue;
        for (const account of [...r.stations, ...r.vending]) account.pending = 0;
        total += amount; airports.push(r.airportId);
    }
    if (total) career(g).retainedAirports = JSON.stringify(records);
    return { total, airports };
}

/** Run every managed retained airport for elapsed simulation seconds at its own policy. Frozen (Act I) airports never change. */
export function advanceRetainedAirports(g: GameModel, seconds: number): void {
    const records = retainedAirports(g);
    if (!records.some(r => r.managed)) return;
    const prestige = career(g).prestige;
    career(g).retainedAirports = JSON.stringify(records.map(r => { const next = advanceManaged(r, seconds, { prestige }); return next.ok ? next.record : r; }));
}

/** Wall-clock absence for managed retained airports when a career loads. */
export function catchUpRetainedAirports(records: TerminalRecord[], elapsedMs: number, prestige: number): TerminalRecord[] {
    return records.map(r => { const next = catchUpManaged(r, elapsedMs, { prestige }); return next.ok ? next.record : r; });
}
